import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { HiOutlineSearch, HiChevronDown, HiChevronUp } from "react-icons/hi";
import { BiExport } from "react-icons/bi";
import { MdClear } from "react-icons/md";
import Button from "../Utilities/Button";
import PaginationNew from "../Utilities/PaginationNew";
import Modal from "../Utilities/Modal";
import Toast from "../Utilities/Toast";
import Datepicker from "../Utilities/Datepicker/Datepicker";
import RightPanelLayout from "../Layout/RightPanelLayout";
import StatusBadge from "./StatusBadge";
import {
  fetchInquiryForms,
  fetchInquiryFormDetail,
  fetchInquiryPlans,
  updateInquiryFormStatus,
  exportInquiryForms,
  setFilter,
  setPage,
  resetDetail,
  selectInquiry,
} from "../../store/slice/manager/inquiry";
import styles from "./style/KeystoneManagement.module.scss";

const LIMIT = 10;

// inquiry_forms.status enum → 列表狀態欄 badge
// undo=未處理 / todo=已聯繫（開啟更多資訊後自動標記）/ complete=已完成
const STATUS_META = {
  undo: { tone: "off", label: "未處理" },
  todo: { tone: "trial", label: "處理中" },
  complete: { tone: "on", label: "已完成" },
};

// 狀態篩選下拉選項（value 對應後端 status enum）
const STATUS_FILTER_OPTIONS = [
  { value: "", label: "全部狀態" },
  { value: "undo", label: "未處理" },
  { value: "todo", label: "處理中" },
  { value: "complete", label: "已完成" },
];

const splitDateTime = (value) => {
  if (!value) {
    return { date: "", time: "" };
  }
  const [date, time = ""] = String(value).split(" ");
  return { date, time };
};

// Date 物件 → "YYYY-MM-DD"（給匯出的日期區間用）
const toYmd = (d) => {
  if (!d) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// 缺值時統一顯示「—」
const show = (value) =>
  value === null || value === undefined || value === "" ? "—" : value;

// ⚠️ 欄位對應：API（inquiry_forms）目前沒有「姓名 / 獨立公司」欄位，
//    只有 campaign_name。以下讀取採「未來 migrate 後的欄位名 → 現有欄位」的 fallback，
//    等 migration 補上欄位後即自動生效，不需再改這裡。
const pickName = (row) => row?.name ?? "";
const pickCompany = (row) => row?.company_name ?? row?.campaign_name ?? "";

const ConsultationList = () => {
  const dispatch = useDispatch();
  const {
    rows,
    total,
    page,
    loading,
    filter,
    detail,
    detailLoading,
    planPackages,
  } = useSelector(selectInquiry);

  // 只有 search 文字框需要 local draft（避免每打一個字都打 API），送出才套用
  const [draftSearch, setDraftSearch] = useState(filter.search || "");
  const [detailOpen, setDetailOpen] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [toast, setToast] = useState({ show: false, title: "", error: false });

  // 匯出列：可挑狀態 + 申請時間區間
  const [exportStatus, setExportStatus] = useState("");
  const [exportFrom, setExportFrom] = useState(null);
  const [exportTo, setExportTo] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!toast.show) return;
    const t = setTimeout(() => setToast((p) => ({ ...p, show: false })), 2500);
    return () => clearTimeout(t);
  }, [toast.show]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / LIMIT)),
    [total]
  );

  const load = useCallback(() => {
    dispatch(fetchInquiryForms({ filter, page, limit: LIMIT }));
  }, [dispatch, filter, page]);

  // 列表：進頁 / 搜尋 / 換頁時重新取得
  useEffect(() => {
    load();
  }, [load]);

  // 方案名稱對照表：一次取回，供 modal 的「感興趣的方案」使用
  useEffect(() => {
    dispatch(fetchInquiryPlans());
  }, [dispatch]);

  const applySearch = () => {
    dispatch(setFilter({ search: draftSearch.trim() }));
  };

  const clearSearch = () => {
    setDraftSearch("");
    dispatch(setFilter({ search: "" }));
  };

  // 申請時間排序：點表頭切換 由新到舊(desc) ⇆ 由舊到新(asc)
  const sortDir = filter.sort_dir || "desc";
  const toggleSort = () => {
    dispatch(setFilter({ sort_dir: sortDir === "desc" ? "asc" : "desc" }));
  };

  // 匯出：依匯出列選的狀態 + 申請時間區間直接下載 xlsx
  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await dispatch(
        exportInquiryForms({
          filter: {
            status: exportStatus,
            date_from: toYmd(exportFrom),
            date_to: toYmd(exportTo),
            sort_dir: sortDir,
          },
        })
      ).unwrap();
      setToast({ show: true, title: "匯出完成", error: false });
    } catch (e) {
      setToast({ show: true, title: "匯出失敗，請稍後再試", error: true });
    } finally {
      setExporting(false);
    }
  };

  const handlePageChange = (event) => {
    const next =
      event && typeof event === "object" && "selected" in event
        ? event.selected + 1
        : Number(event);
    if (Number.isInteger(next) && next > 0) {
      dispatch(setPage(next));
    }
  };

  // 更多資訊被點開 → 取得詳情，並直接標記為已聯繫（status: undo → todo）
  const handleOpenDetail = async (row) => {
    setDetailOpen(true);
    const action = await dispatch(fetchInquiryFormDetail(row.id));
    if (
      fetchInquiryFormDetail.fulfilled.match(action) &&
      action.payload?.status === "undo"
    ) {
      dispatch(updateInquiryFormStatus({ id: row.id, status: "todo" }));
    }
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    dispatch(resetDetail());
  };

  // 任務完成 → status: complete
  const handleComplete = async () => {
    if (!detail || completing) return;
    setCompleting(true);
    try {
      await dispatch(
        updateInquiryFormStatus({ id: detail.id, status: "complete" })
      ).unwrap();
      setToast({ show: true, title: "已標記為任務完成", error: false });
      handleCloseDetail();
    } catch (e) {
      setToast({ show: true, title: "更新失敗，請稍後再試", error: true });
    } finally {
      setCompleting(false);
    }
  };

  // detail 的方案 / 時間 / 狀態
  const selectedPlanIds = useMemo(
    () => new Set((detail?.plan_ids ?? []).map(Number)),
    [detail]
  );
  // chip 是方案（package）層，一個方案一個 chip；單據存的 plan_ids 是方案底下的價格檔位，
  // 命中任一檔位就算選到該方案。已停用／已刪除的方案（selectable=false）不再是可選項，
  // 只有該單有選到時才顯示。
  const visiblePlans = useMemo(
    () =>
      planPackages
        .map((pkg) => ({
          id: pkg.id,
          name: pkg.name,
          available: pkg.selectable,
          selected: (pkg.plan_ids ?? []).some((id) =>
            selectedPlanIds.has(Number(id))
          ),
        }))
        .filter((pkg) => pkg.available || pkg.selected),
    [planPackages, selectedPlanIds]
  );
  const timelineValue = detail?.expect_date_range ?? "";
  const isComplete = detail?.status === "complete";

  const detailName = pickName(detail);
  const detailCompany = pickCompany(detail);
  const modalTitle =
    [detailName, detailCompany].filter(Boolean).join(" · ") ||
    detail?.email ||
    "諮詢詳情";

  return (
    <RightPanelLayout title="諮詢管理">
      <div className={styles.listToolbar}>
        <div className={styles.listToolbarTitle}>諮詢列表</div>
        <div className={styles.toolbarActions}>
          <select
            className={styles.select}
            value={filter.status}
            onChange={(e) => dispatch(setFilter({ status: e.target.value }))}
            aria-label="狀態篩選"
          >
            {STATUS_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <div className={styles.searchBar}>
            <button
              type="button"
              className={styles.searchBarIconBtn}
              onClick={applySearch}
              aria-label="搜尋"
            >
              <HiOutlineSearch className={styles.searchBarIcon} />
            </button>
            <input
              type="search"
              className={styles.searchBarInput}
              value={draftSearch}
              placeholder="搜尋姓名、企業名稱、Email 或手機"
              onChange={(e) => setDraftSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  applySearch();
                }
              }}
              aria-label="搜尋姓名、企業名稱、Email 或手機"
            />
            {draftSearch ? (
              <button
                type="button"
                className={styles.searchBarClear}
                onClick={clearSearch}
                aria-label="清除搜尋"
              >
                <MdClear className={styles.searchBarClearIcon} />
              </button>
            ) : null}
          </div>
          <Button type="primary" title="搜尋" onClick={applySearch} />
        </div>
      </div>

      {/* 第二行：匯出條件（狀態 + 申請時間區間）＋匯出按鈕 */}
      <div className={styles.exportBar}>
        <span className={styles.exportBarLabel}>匯出條件</span>
        <select
          className={styles.select}
          value={exportStatus}
          onChange={(e) => setExportStatus(e.target.value)}
          aria-label="匯出狀態"
        >
          {STATUS_FILTER_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <div className={styles.datepickerCell}>
          <Datepicker
            selectDate={exportFrom}
            setSelectDate={setExportFrom}
            placeholderText="起始日"
            dateFormat="yyyy-MM-dd"
          />
        </div>
        <span className={styles.dateRangeSep}>~</span>
        <div className={styles.datepickerCell}>
          <Datepicker
            selectDate={exportTo}
            setSelectDate={setExportTo}
            placeholderText="結束日"
            dateFormat="yyyy-MM-dd"
          />
        </div>
        <Button
          type="primary"
          title={exporting ? "匯出中…" : "匯出"}
          icon={<BiExport size={16} />}
          onClick={handleExport}
          disabled={exporting}
        />
      </div>

      {total > 0 ? (
        <div className={styles.paginationTop}>
          <PaginationNew
            onPageChange={handlePageChange}
            pageCount={page}
            limit={LIMIT}
            totalPages={totalPages}
            totalData={total}
          />
        </div>
      ) : null}

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>
                <button
                  type="button"
                  className={styles.sortHeader}
                  onClick={toggleSort}
                  aria-label={
                    sortDir === "desc"
                      ? "申請時間：目前由新到舊，點擊改由舊到新"
                      : "申請時間：目前由舊到新，點擊改由新到舊"
                  }
                >
                  申請時間
                  {sortDir === "desc" ? (
                    <HiChevronDown className={styles.sortIcon} />
                  ) : (
                    <HiChevronUp className={styles.sortIcon} />
                  )}
                </button>
              </th>
              <th>姓名</th>
              <th>企業名稱</th>
              <th>職位</th>
              <th>手機</th>
              <th>電子郵件</th>
              <th>狀態</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className={styles.tableEmptyCell} colSpan={8}>
                  載入中…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td className={styles.tableEmptyCell} colSpan={8}>
                  尚無諮詢資料
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const { date, time } = splitDateTime(row.created_at);
                const statusMeta = STATUS_META[row.status] || STATUS_META.undo;
                return (
                  <tr key={row.id}>
                    <td>
                      <div className={styles.cellStrong}>{date}</div>
                      <div className={styles.cellSub}>{time}</div>
                    </td>
                    <td className={styles.cellStrong}>{show(pickName(row))}</td>
                    <td className={styles.cellStrong}>
                      {show(pickCompany(row))}
                    </td>
                    <td>{show(row.position)}</td>
                    <td>{show(row.phone)}</td>
                    <td>{show(row.email)}</td>
                    <td>
                      <StatusBadge
                        tone={statusMeta.tone}
                        label={statusMeta.label}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className={styles.moreBtn}
                        onClick={() => handleOpenDetail(row)}
                      >
                        更多資訊
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={detailOpen}
        size="md"
        width="min(640px, calc(100vw - 32px))"
      >
        {detailOpen ? (
          <div className={styles.modalRoot}>
            <div className={styles.modalHead}>
              <div>
                <h3 className={styles.modalTitle}>{modalTitle}</h3>
                <p className={styles.modalSub}>
                  申請時間 {show(detail?.created_at)}
                </p>
              </div>
              <button
                type="button"
                className={styles.modalClose}
                onClick={handleCloseDetail}
                aria-label="關閉"
              >
                ✕
              </button>
            </div>

            <div className={styles.modalBody}>
              {detailLoading && !detail ? (
                <div className={styles.tableEmptyCell}>載入中…</div>
              ) : (
                <>
                  <div className={styles.dividerLabel}>基本資訊</div>
                  <div className={styles.infoGrid}>
                    <div>
                      <div className={styles.infoKey}>姓名</div>
                      <div className={styles.infoVal}>{show(detailName)}</div>
                    </div>
                    <div>
                      <div className={styles.infoKey}>企業名稱</div>
                      <div className={styles.infoVal}>{show(detailCompany)}</div>
                    </div>
                    <div>
                      <div className={styles.infoKey}>職位</div>
                      <div className={styles.infoVal}>
                        {show(detail?.position)}
                      </div>
                    </div>
                    <div>
                      <div className={styles.infoKey}>員工人數</div>
                      <div className={styles.infoVal}>
                        {detail?.number_of_employees
                          ? `${detail.number_of_employees} 人`
                          : "—"}
                      </div>
                    </div>
                    <div>
                      <div className={styles.infoKey}>聯絡電話</div>
                      <div className={styles.infoVal}>{show(detail?.phone)}</div>
                    </div>
                    <div>
                      <div className={styles.infoKey}>電子郵件</div>
                      <div className={styles.infoVal}>{show(detail?.email)}</div>
                    </div>
                  </div>

                  <div className={styles.dividerLabel}>
                    感興趣的方案（可複選）
                  </div>
                  <div className={styles.chipGrid}>
                    {visiblePlans.length === 0 ? (
                      <span className={styles.cellMuted}>尚無方案資料</span>
                    ) : (
                      visiblePlans.map((plan) => (
                        <span
                          key={plan.id}
                          className={`${styles.chip} ${
                            plan.selected ? styles.chipSelected : ""
                          }`}
                        >
                          {plan.name}
                        </span>
                      ))
                    )}
                  </div>

                  <div className={styles.dividerLabel}>預計導入時間</div>
                  <div className={styles.timelineGrid}>
                    {timelineValue ? (
                      <span
                        className={`${styles.timelineChip} ${styles.timelineChipSelected}`}
                      >
                        {timelineValue}
                      </span>
                    ) : (
                      <span className={styles.cellMuted}>—</span>
                    )}
                  </div>

                  <div className={styles.dividerLabel}>想了解或解決的問題</div>
                  <div className={styles.noteBox}>
                    {detail?.inquiry_question || "—"}
                  </div>
                </>
              )}
            </div>

            <div className={styles.modalFoot}>
              <button
                type="button"
                className={styles.btnGhost}
                onClick={handleCloseDetail}
              >
                關閉
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleComplete}
                disabled={isComplete || completing}
              >
                {isComplete ? "已完成" : "任務完成"}
              </button>
            </div>
          </div>
        ) : null}
      </Modal>

      <Toast show={toast.show} title={toast.title} error={toast.error} />
    </RightPanelLayout>
  );
};

export default ConsultationList;
